// /Volumes/KINGSTON/new-react-project/the-ultimate-guide/src/components/Review/NoteInputBox.js
import React, { useState, useRef, useEffect, useMemo } from "react";
import { db, storage, auth } from "../../Security/firebase-config";
import { collection, Timestamp, doc, setDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useTeam } from "../../context/TeamContext";
import useTeamQuotaStatus from "../../hooks/useTeamQuotaStatus";
import MentionMenu from "./MentionMenu";
import {addPlayerMentions} from './playerMentions';
import "./NoteInputBox.css";

import { ensureActiveTeamClaim } from "../../utils/authClaims";
import {
  checkTeamQuotaAllowance,
  confirmTrackedUploadFinalize,
} from "../../utils/teamQuota";

// same as MentionDropdown
const normalizeRole = (raw) => {
  const r = (raw || "player").toString().trim().toLowerCase();
  if (r === "subs" || r === "substitute" || r === "substitutes") return "sub";
  if (r === "manger" || r === "managers") return "manager";
  if (r === "spectators") return "spectator";
  if (r === "players") return "player";
  if (r === "trialing" || r === "trials") return "trial";
  return r;
};

// ── Mention extraction helper (source of truth for Firestore "at" field) ──
export const extractMentionsFromText = (text, { members, byRole, coachingStaff }) => {
  const out = new Set();
  const t = String(text || "");

  // 1) @Team (case-insensitive)
  if (/@team\b/i.test(t)) out.add("@Team");

  // 2) Group labels (case-insensitive)
  if (/@\s*coaching\s+staff\b/i.test(t)) {
    (coachingStaff || []).forEach((m) => out.add(`@${m.username}`));
  }
  if (/@\s*subs\b/i.test(t)) {
    (byRole?.sub || []).forEach((m) => out.add(`@${m.username}`));
  }
  if (/@\s*managers\b/i.test(t)) {
    (byRole?.manager || []).forEach((m) => out.add(`@${m.username}`));
  }

  // 3) Individual @username tokens (only keep real team usernames)
  const usernameSet = new Set(
    (members || []).map((m) => String(m.username || "").toLowerCase())
  );

  const tokens = t.match(/@([A-Za-z0-9_]+)/g) || [];
  for (const tok of tokens) {
    const uname = tok.slice(1).toLowerCase();
    if (usernameSet.has(uname)) {
      const canonical =
        (members || []).find((m) => m.username?.toLowerCase() === uname)?.username ||
        tok.slice(1);
      out.add(`@${canonical}`);
    }
  }

  return addPlayerMentions(t, byRole?.player, Array.from(out));
};

export default function NoteInputBox({
  mapId,
  matchId,
  matchTimeSec,
  onHide,
  onBeginTyping,
  onPosted,
  localSubmit,
  requireMention = true,
  teamIdOverride,
  usernameOverride,
  membersOverride,
  draftScope,
  attachmentMaxBytes = Infinity,
}) {
  const context = useTeam();
  const teamId = teamIdOverride ?? context.teamId;
  const username = usernameOverride ?? context.username;
  const isLocalMode = typeof localSubmit === "function";
  const quotaStatus = useTeamQuotaStatus(isLocalMode ? null : teamId);
  const quotaExceeded = !isLocalMode && quotaStatus.uploadBlocked;

  // ✅ Draft should be match-scoped so it survives POV switching
  const draftKey = useRef(
    draftScope || `noteDraft:${teamId || "no-team"}:${mapId || "no-map"}:${matchId || "no-match"}`
  );

  useEffect(() => {
    const handler = (e) => {
      const imageDataURL = e.detail;
      fetch(imageDataURL)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "screenshot.png", { type: "image/png" });
          setSelectedFile(file);
          setMediaTitle("Screenshot");
        });
    };
    window.addEventListener("screenshotCaptured", handler);
    return () => window.removeEventListener("screenshotCaptured", handler);
  }, []);

  const [note, setNote] = useState("");
  const [mentions, setMentions] = useState([]);
  const [importance, setImportance] = useState(1);
  const [tags, setTags] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaTitle, setMediaTitle] = useState("");
  const fileInputRef = useRef();
  const [isSaving, setIsSaving] = useState(false);
  const [saveStage, setSaveStage] = useState("idle");

  const hasNotifiedTypingRef = useRef(false);

  const [alertInfo, setAlertInfo] = useState({ message: "", type: "info" });

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(draftKey.current);
      if (raw) {
        const d = JSON.parse(raw);
        if (typeof d.note === "string") setNote(d.note);
        if (Array.isArray(d.mentions)) setMentions(d.mentions);
        if (typeof d.tags === "string") setTags(d.tags);
        if (typeof d.importance === "number") setImportance(d.importance);
        if (typeof d.mediaTitle === "string") setMediaTitle(d.mediaTitle);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save draft (debounced)
  const draftSaveTimer = useRef(null);
  useEffect(() => {
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      const snapshot = { note, mentions, tags, importance, mediaTitle };
      try {
        sessionStorage.setItem(draftKey.current, JSON.stringify(snapshot));
      } catch {}
    }, 250);
    return () => clearTimeout(draftSaveTimer.current);
  }, [note, mentions, tags, importance, mediaTitle]);

  const showAlert = (message, type = "info") => setAlertInfo({ message, type });
  const saveButtonLabel =
    isSaving && saveStage === "uploading_image"
      ? "Uploading image..."
      : isSaving
      ? "Saving..."
      : "Save Note";
  const saveStatusMessage =
    isSaving && saveStage === "uploading_image"
      ? "Uploading image..."
      : isSaving
      ? "Saving note..."
      : "";

  // ✅ Firestore collection reference for MATCH notes (shared across POVs)
  const notesCollectionRef = useMemo(() => {
    if (!teamId || !mapId || !matchId) return null;
    return collection(
      db,
      "teams",
      teamId,
      "maps",
      mapId,
      "matches",
      matchId,
      "notes"
    );
  }, [teamId, mapId, matchId]);

  // ── Team members (for inline mention suggestions) ──
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (membersOverride) {
      setMembers(membersOverride.map(m => ({...m, roleLower: normalizeRole(m.roleLower || m.role)})));
      return;
    }
    if (!teamId) return;
    (async () => {
      const teamRef = doc(db, "teams", teamId);
      const teamSnap = await getDoc(teamRef);
      if (!teamSnap.exists()) return;
      const data = teamSnap.data() || {};
      const memberIds = data.members || [];
      const teamRoles = data.teamRoles || {};
      const rows = await Promise.all(
        memberIds.map(async (uid) => {
          const uRef = doc(db, "uids", uid);
          const uSnap = await getDoc(uRef);
          if (!uSnap.exists()) return null;
          const username = uSnap.data().username;
          if (!username) return null;
          return {
            uid,
            username,
            roleLower: normalizeRole(teamRoles[uid] || "player"),
          };
        })
      );
      setMembers(rows.filter(Boolean));
    })();
  }, [teamId, membersOverride]);

  useEffect(() => {
    const root = document.querySelector(".note-input-box");
    if (!root) return;

    const handler = (e) => {
      // capture phase so it runs before React
      console.log(
        "[NoteInputBox] CAPTURE click target:",
        e.target,
        "class=",
        e.target?.className
      );
    };

    root.addEventListener("click", handler, true);
    return () => root.removeEventListener("click", handler, true);
  }, []);

  const byRole = useMemo(() => {
    const base = {
      player: [],
      sub: [],
      analyst: [],
      coach: [],
      manager: [],
      spectator: [],
      trial: [],
    };
    members.forEach((m) => {
      (base[m.roleLower] ||= []).push(m);
    });
    Object.keys(base).forEach((k) =>
      base[k].sort((a, b) => a.username.localeCompare(b.username))
    );
    return base;
  }, [members]);

  const coachingStaff = useMemo(
    () =>
      [...byRole.analyst, ...byRole.coach].sort((a, b) =>
        a.username.localeCompare(b.username)
      ),
    [byRole.analyst, byRole.coach]
  );

  const baseItems = useMemo(() => {
    let id = 0;
    const items = [];
    items.push({ id: id++, label: "@Players", kind: "group", group: "players", payload: {members: byRole.player} });
    byRole.player.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );
    if (coachingStaff.length)
      items.push({
        id: id++,
        label: "@ Coaching Staff",
        kind: "group",
        group: "coaching",
        payload: { members: coachingStaff },
      });
    if (byRole.sub.length)
      items.push({
        id: id++,
        label: "@ Subs",
        kind: "group",
        group: "subs",
        payload: { members: byRole.sub },
      });
    byRole.sub.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );
    if (byRole.manager.length)
      items.push({
        id: id++,
        label: "@ Managers",
        kind: "group",
        group: "managers",
        payload: { members: byRole.manager },
      });
    byRole.manager.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );
    byRole.spectator.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );
    byRole.trial.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );
    return items;
  }, [byRole, coachingStaff]);

  // ── Inline mention popover state/logic ──
  const textareaRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [trigger, setTrigger] = useState({ start: -1, query: "" });

  const suggestions = useMemo(() => {
    const q = (trigger.query || "").trim().toLowerCase();
    if (!q) return baseItems;
    return baseItems.filter((it) =>
      it.label.replace(/^@ ?/, "").toLowerCase().startsWith(q)
    );
  }, [baseItems, trigger.query]);

  const updateMenuPosition = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const parent = ta.parentElement; // <div class="note-input-box">
    const taRect = ta.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    setMenuPos({
      top: taRect.bottom - parentRect.top + 6,
      left: taRect.left - parentRect.left + 12,
    });
  };

  const onTextChangeInline = (e) => {
    const val = e.target.value;

    // freeze note timer the instant user starts typing (only once)
    if (!hasNotifiedTypingRef.current && val.length > 0) {
      hasNotifiedTypingRef.current = true;
      onBeginTyping?.();
    }

    if (val.length <= 425) setNote(val);
    const caret = e.target.selectionStart || 0;
    const uptoCaret = val.slice(0, caret);
    const match = uptoCaret.match(/(?:^|\s)@([A-Za-z0-9_]*)$/);
    if (match) {
      const q = match[1];
      const atPos = caret - (q.length + 1);
      setTrigger({ start: atPos, query: q });
      setMenuOpen(true);
      setActiveIndex(0);
      updateMenuPosition();
    } else {
      setMenuOpen(false);
      setTrigger({ start: -1, query: "" });
    }
  };

  const applyPick = (item) => {
    const ta = textareaRef.current;
    if (!ta || trigger.start < 0) return;

    const before = note.slice(0, trigger.start);
    const after = note.slice(ta.selectionStart);

    const addMention = (tag) =>
      setMentions((prev) => (prev.includes(tag) ? prev : [...prev, tag]));

    let insertText = "";
    if (item.kind === "team") {
      insertText = "@Team";
      addMention("@Team");
    } else if (item.kind === "group") {
      insertText = item.label;
      item.payload.members.forEach((m) => addMention(`@${m.username}`));
    } else {
      insertText = `@${item.payload.username}`;
      addMention(insertText);
    }

    const next = `${before}${insertText} ${after}`;
    setNote(next);
    const newPos = (before + insertText + " ").length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newPos, newPos);
    });

    setMenuOpen(false);
    setTrigger({ start: -1, query: "" });
  };

  const onTextKeyDownInline = (e) => {
    if (!menuOpen) return;
    if (["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key))
      e.preventDefault();

    if (e.key === "ArrowDown")
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    else if (e.key === "ArrowUp") setActiveIndex((i) => Math.max(i - 1, 0));
    else if (e.key === "Escape") {
      setMenuOpen(false);
    } else if (e.key === "Enter" || e.key === "Tab") {
      const pick = suggestions[activeIndex] || suggestions[0];
      if (pick) applyPick(pick);
    }
  };

  // Ensure manually-typed @names count as mentions at submit time
  const syncMentionsFromText = () => {
    const computed = extractMentionsFromText(note, {
      members,
      byRole,
      coachingStaff,
    });
    setMentions(computed);
    return computed;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      // freeze timer the moment an image is chosen (only once)
      if (!hasNotifiedTypingRef.current) {
        hasNotifiedTypingRef.current = true;
        onBeginTyping?.();
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setMediaTitle("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (isSaving) return;

    const ensured = syncMentionsFromText();

    if (!isLocalMode && (!teamId || !mapId || !matchId)) {
      return showAlert("Missing team/map/match context.", "error");
    }
    if (!isLocalMode && !notesCollectionRef) {
      return showAlert("Notes are not ready yet. Try again.", "error");
    }

    // ✅ matchTimeSec is required for POV-shared timeline
    const mt = Number(matchTimeSec);
    if (!Number.isFinite(mt)) {
      return showAlert("Match time not ready yet. Try again in a second.", "error");
    }

    if (!note && !selectedFile) {
      return showAlert("Either text or an image is required.", "error");
    }
    if (requireMention && !ensured.length) {
      return showAlert("At least one @mention is required.", "error");
    }
    if (selectedFile && selectedFile.size > attachmentMaxBytes) {
      return showAlert("Choose an image smaller than " + Math.floor(attachmentMaxBytes / 1024) + " KB for this review.", "error");
    }
    if (selectedFile && !mediaTitle.trim()) {
      return showAlert("Please provide a title for your image.", "error");
    }

    // store with sensible precision
    const roundedMatchTimeSec = Math.max(0, Math.round(mt * 10) / 10);

    let sizeBytes;
    let mediaStoragePath = "";

    const noteRef = isLocalMode ? null : doc(notesCollectionRef);
    let downloadURL = "";

    try {
      setAlertInfo({ message: "", type: "info" });
      setIsSaving(true);
      setSaveStage(selectedFile ? "uploading_image" : "saving_note");

      if (isLocalMode) {
        let imageDataUrl = null;
        if (selectedFile) {
          imageDataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === "string") {
                resolve(reader.result);
                return;
              }
              reject(new Error("The selected image could not be read."));
            };
            reader.onerror = () => reject(new Error("The selected image could not be read."));
            reader.readAsDataURL(selectedFile);
          });
        }

        setSaveStage("saving_note");
        await localSubmit({
          text: note,
          mentions: ensured,
          tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
          importance,
          atMs: Math.round(roundedMatchTimeSec * 1000),
          imageDataUrl,
          imageName: selectedFile ? selectedFile.name : null,
          imageTitle: selectedFile ? mediaTitle.trim() : "",
        });
      } else if (selectedFile) {
        const allowance = await checkTeamQuotaAllowance({
          teamId,
          action: "upload_image",
          bytesToAdd: selectedFile.size || 0,
        });

        if (!allowance?.uploadAllowed) {
          return showAlert(
            allowance?.message ||
              quotaStatus.uploadBlockMessage ||
              "Uploading is unavailable right now.",
            "error"
          );
        }

        await ensureActiveTeamClaim(teamId);

        // ✅ store images under match notes so they are shared across POVs
        const storagePath = `teams/${teamId}/maps/${mapId}/matches/${matchId}/notes/${noteRef.id}/${selectedFile.name}`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, selectedFile, {
          contentType: selectedFile.type || "image/png",
          cacheControl: "public, max-age=31536000, immutable",
          contentDisposition: `inline; filename="${selectedFile.name}"`,
          customMetadata: {
            teamId,
            mapId,
            matchId,
            noteId: noteRef.id,
            createdByUid: auth.currentUser.uid || "",
            sourceType: "note_image",
          },
        });

        const finalization = await confirmTrackedUploadFinalize({
          teamId,
          objectPath: storageRef.fullPath,
        });

        if (!finalization?.accepted) {
          throw new Error(
            finalization?.message ||
              "Upload was rejected by the current team quota rules."
          );
        }

        downloadURL = await getDownloadURL(storageRef);
        sizeBytes = selectedFile.size;
        mediaStoragePath = storageRef.fullPath;
      }

      if (noteRef) {
        setSaveStage("saving_note");

      await setDoc(noteRef, {
        text: note,
        at: ensured,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        importance,

        // ✅ NEW source of truth for time
        matchTimeSec: roundedMatchTimeSec,

        createdBy: username,
        createdByUid: auth.currentUser.uid,
        viewers: [username],
        createdAt: Timestamp.now(),

        ...(selectedFile
          ? {
              mediaTitle: mediaTitle.trim(),
              mediaURL: downloadURL,
              mediaStoragePath,
              sizeBytes,
            }
          : {}),
        });
      }
    } catch (error) {
      return showAlert(
        error?.message || "Failed to save the note attachment.",
        "error"
      );
    } finally {
      setIsSaving(false);
      setSaveStage("idle");
    }

    try {
      sessionStorage.removeItem(draftKey.current);
    } catch {}

    setNote("");
    setMentions([]);
    setTags("");
    setImportance(1);
    setSelectedFile(null);
    setMediaTitle("");
    if (fileInputRef.current) fileInputRef.current.value = "";

    // allow next note to freeze again, and tell parent to go live
    hasNotifiedTypingRef.current = false;
    onPosted?.();
  };

  return (
    <div className="note-input-box" style={{ position: "relative" }}>
      {alertInfo?.message ? (
        <div className={`note-alert ${alertInfo.type}`}>
          {alertInfo.message}
        </div>
      ) : null}
      
      <textarea
        ref={textareaRef}
        value={note}
        onChange={onTextChangeInline}
        onKeyDown={onTextKeyDownInline}
        onFocus={updateMenuPosition}
        placeholder="Write a note… type @ to mention"
        disabled={isSaving}
      />

      {menuOpen && (
        <MentionMenu
          items={suggestions}
          open={menuOpen}
          style={{ ...menuPos, zIndex: 50 }}
          activeIndex={activeIndex}
          onHoverIndex={(i) => setActiveIndex(i)}
          onPick={applyPick}
        />
      )}

      <div className="char-count">{note.length} / 425</div>

      <input
        type="text"
        placeholder="Tags (comma-separated)"
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        disabled={isSaving}
      />

      <div className="media-upload-section">
        <label className={`media-upload-label ${quotaExceeded ? "exceeded" : ""}`}>
          Add Image (optional):
        </label>

        <div className="file-upload-wrapper">
          <label
            htmlFor="media-file-input"
            className={`file-upload-btn ${quotaExceeded ? "exceeded" : ""} ${
              isSaving ? "disabled" : ""
            }`}
          >
            {quotaExceeded ? "Choice Disabled" : "Choose File"}
          </label>

          <input
            id="media-file-input"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            ref={fileInputRef}
            disabled={quotaExceeded || isSaving}
            className="file-input-hidden"
          />

          <span className={`file-upload-text${quotaExceeded ? " exceeded" : ""}`}>
            {quotaExceeded
              ? quotaStatus.uploadBlockMessage || "Uploading is unavailable right now."
              : selectedFile
              ? selectedFile.name
              : "No file chosen"}
          </span>

          {selectedFile && !quotaExceeded && (
            <button
              type="button"
              className="remove-image-btn"
              onClick={handleRemoveFile}
              aria-label="Remove selected image"
              disabled={isSaving}
            >
              🗑️
            </button>
          )}
        </div>

        {selectedFile && (
          <input
            type="text"
            placeholder="Media Title"
            value={mediaTitle}
            onChange={(e) => setMediaTitle(e.target.value)}
            className="media-title-input"
            disabled={isSaving}
          />
        )}
      </div>

      <div className="importance-save-row">
        <div className="importance-selector">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              onClick={() => !isSaving && setImportance(n)}
              className={`${importance === n ? "selected" : ""} ${
                isSaving ? "disabled" : ""
              }`.trim()}
              aria-disabled={isSaving}
            >
              {"!".repeat(n)}
            </span>
          ))}
        </div>

        <div className="save-action-column">
          <div className="save-status" aria-live="polite">
            {saveStatusMessage}
          </div>
          <button
            type="button"
            className="save-btn"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {saveButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
